import { Avatar } from '@seapoint/client/src/design-system/avatar/avatar/avatar';
import { Checkbox } from '@seapoint/client/src/design-system/checkbox/checkbox';
import { ColumnHeaderWithSorting } from '@seapoint/client/src/design-system/data-table/column-header-with-sorting';
import { Input } from '@seapoint/client/src/design-system/input/input';
import { cn } from '@seapoint/client/src/utils/cn';
import { getCurrencySymbol } from '@seapoint/client/src/utils/get-currency-symbol';
import { ColumnDef } from '@tanstack/react-table';
import { PayrollEmployee } from '../../financial-information-step/financial-information-table-column-definition';

export const recordsTableColumnDefinition: ColumnDef<PayrollEmployee>[] = [
  {
    id: 'select',
    header: ({ table }) => {
      const allIds = table.getRowModel().rows.map((row) => row.original.id);
      const selected = table.options.meta?.selectedRowIds ?? [];
      const allSelected = allIds.length > 0 && allIds.every((id) => selected.includes(id));
      const someSelected = allIds.length > 0 && allIds.some((id) => selected.includes(id)) && !allSelected;

      return (
        <Checkbox
          checked={someSelected ? 'indeterminate' : allSelected}
          onCheckedChange={(checked) => {
            if (checked) {
              table.options.meta?.setSelectedRowIds?.(Array.from(new Set([...selected, ...allIds])));
            } else {
              table.options.meta?.setSelectedRowIds?.(selected.filter((id) => !allIds.includes(id)));
            }
          }}
          aria-label="Select all"
        />
      );
    },
    cell: ({ row, table }) => (
      <Checkbox
        checked={table.options.meta?.selectedRowIds?.includes(row.original.id)}
        onCheckedChange={(checked) => {
          const id = row.original.id;
          const selected = table.options.meta?.selectedRowIds ?? [];
          if (checked) {
            table.options.meta?.setSelectedRowIds?.([...selected, id]);
          } else {
            table.options.meta?.setSelectedRowIds?.(selected.filter((sid) => sid !== id));
          }
        }}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
    size: 8,
    minSize: 8,
  },
  {
    id: 'avatar',
    header: () => null,
    cell: ({ row }) => {
      const employee = row.original;
      const fullName = `${employee.firstName} ${employee.lastName}`;
      return <Avatar name={fullName} size="sm" />;
    },
    enableSorting: false,
    enableHiding: false,
    size: 10,
    minSize: 10,
  },
  {
    accessorKey: 'firstName',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="First Name" />,
    cell: ({ row }) => <span>{row.original.firstName}</span>,
    size: 20,
    minSize: 20,
  },
  {
    accessorKey: 'lastName',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Last Name" />,
    cell: ({ row }) => <span>{row.original.lastName}</span>,
    size: 20,
    minSize: 20,
  },
  {
    accessorKey: 'netPay',
    id: 'netPay',
    header: ({ column }) => (
      <div className="flex justify-end">
        <ColumnHeaderWithSorting column={column} label="Net Pay" />
      </div>
    ),
    cell: ({ row, table }) => {
      const employee = row.original;
      const currencySymbol = getCurrencySymbol(table.options.meta?.baseCurrency);
      const onNetPayChange = table.options.meta?.onNetPayChange;

      return (
        <Input
          type="text"
          value={employee.netPay ? `${currencySymbol}${employee.netPay}` : ''}
          onChange={(e) => {
            const value = e.target.value;
            // Remove currency symbol and non-numeric chars, then validate
            const stringValue = value.replace(currencySymbol, '').replace(/[^\d.]/g, '');

            // Allow empty string or valid decimal numbers with up to 2 decimal places
            if (stringValue === '' || /^\d*\.?\d{0,2}$/.test(stringValue)) {
              if (onNetPayChange) {
                onNetPayChange(employee.id, stringValue);
              }
            }
          }}
          onFocus={(e) => {
            if (e.target.value === currencySymbol + '0') {
              e.target.value = currencySymbol;
            }
          }}
          placeholder={`${currencySymbol}0.00`}
          className={cn(
            'border-seapoint-gray-border w-full rounded-lg bg-white text-right text-sm',
            '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
          )}
          // Prevent row click when clicking on input
          onClick={(e) => e.stopPropagation()}
        />
      );
    },
    size: 20,
    minSize: 20,
  },
];
